/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    TextField,
    CircularProgress,
    Alert,
    Stack,
    FormControlLabel,
    Checkbox,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'https://aqa.wtdev.dev';

const CleanupModal = () => {
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState('');
    const [isCleanupAll, setIsCleanupAll] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const resetState = () => {
        setDate('');
        setIsCleanupAll(false);
        setError(null);
        setWarning(null);
        setSuccess(null);
    };

    const handleDelete = async () => {
        setLoading(true);
        setError(null);
        setWarning(null);
        setSuccess(null);

        try {
            const resultsRes = await fetch(`${API_URL}/api/delete-results`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ date, isCleanupAll }),
            });

            const screenshotsRes = await fetch(`${API_URL}/api/delete-screenshots`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ date, isCleanupAll }),
            });

            const errors = [];
            const warnings = [];

            if (!resultsRes.ok) {
                const err = await resultsRes.text();
                errors.push(`Results: ${err}`);
            } else {
                setSuccess('Test results deleted successfully');
            }

            if (!screenshotsRes.ok) {
                const warn = await screenshotsRes.text();
                warnings.push(`Screenshots: ${warn}`);
            }

            if (errors.length > 0) {
                setError(errors.join(', '));
            }

            if (warnings.length > 0) {
                setWarning(warnings.join(', '));
            }

            if (errors.length === 0 && warnings.length === 0) {
                setSuccess('Successfully removed old data');
            }

        } catch (err: any) {
            setError(`Unexpected error: ${err.message}`);
        }
        setLoading(false);
    };

    return (
        <>
            <Button variant="outlined" onClick={() => setOpen(true)} sx={{ mb: 2, ml: 2 }}>
                Cleanup
            </Button>
            <Modal
                open={open}
                onClose={() => {
                    setOpen(false);
                    resetState();
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: '#1a1d24',
                        color: '#f1f1f1',
                        p: 4,
                        borderRadius: 2,
                        minWidth: 300,
                        boxShadow: 24,
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Cleanup old test results
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fdd835', mb: 2 }}>
                        Be careful, removing data can affect dashboard views if date will be in 3 days range
                    </Typography>
                    <TextField
                        fullWidth
                        label="Date"
                        placeholder="Fill date in 'yyyy-mm-dd' format"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        sx={{
                            mt: 2,
                            input: { color: '#f1f1f1', fontSize: '1.2rem', padding: '14px' },
                            '& label': { color: '#f1f1f1', fontSize: '1.2rem' },
                        }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isCleanupAll}
                                onChange={(e) => setIsCleanupAll(e.target.checked)}
                                sx={{ color: '#f1f1f1' }}
                            />
                        }
                        label="Cleanup all data before selected date"
                        sx={{ mt: 1, '& .MuiFormControlLabel-label': { color: '#ef5350' } }}
                    />

                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            disabled={!date || loading}
                            onClick={handleDelete}
                            sx={{ bgcolor: '#e57373', '&:hover': { bgcolor: '#ef5350' } }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Delete test results'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setOpen(false);
                                resetState();
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        {error && <Alert severity="error">{error}</Alert>}
                        {success && <Alert severity="success">{success}</Alert>}
                        {warning && <Alert severity="warning">{warning}</Alert>}
                    </Stack>
                </Box>
            </Modal>
        </>
    );
};

export default CleanupModal;
